import { useId } from "react";

type TxSenderDiagramProps = {
  className?: string;
  /** Decorative thumbnail — hide from AT, parent already names the project */
  decorative?: boolean;
  title?: string;
};

export function TxSenderDiagram({
  className,
  decorative,
  title,
}: TxSenderDiagramProps) {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const arrowId = `${uid}-arrow`;
  const titleId = `${uid}-title`;
  const label = title ?? "How Orca TX Sender lands a Solana transaction";

  return (
    <svg
      className={className}
      viewBox="0 0 1600 1000"
      xmlns="http://www.w3.org/2000/svg"
      role={decorative ? undefined : "img"}
      aria-hidden={decorative ? true : undefined}
      aria-labelledby={decorative ? undefined : titleId}
    >
      {decorative ? null : <title id={titleId}>{label}</title>}
      <defs>
        <marker
          id={arrowId}
          markerWidth="12"
          markerHeight="12"
          refX="10"
          refY="6"
          orient="auto"
          markerUnits="userSpaceOnUse"
        >
          <path d="M0 0 L12 6 L0 12 Z" fill="var(--color-accent)" />
        </marker>
      </defs>

      <rect width="1600" height="1000" fill="var(--color-bg-muted)" />

      <text
        x="800"
        y="78"
        textAnchor="middle"
        fill="var(--color-accent)"
        fontFamily="var(--font-mono)"
        fontSize="26"
        letterSpacing="0.18em"
      >
        HOW IT WORKS
      </text>
      <text
        x="800"
        y="148"
        textAnchor="middle"
        fill="var(--color-text)"
        fontFamily="var(--font-display)"
        fontSize="64"
        fontWeight="550"
      >
        How a transaction lands
      </text>

      <line
        x1="282"
        y1="268"
        x2="1324"
        y2="268"
        stroke="var(--color-accent)"
        strokeWidth="4"
        markerEnd={`url(#${arrowId})`}
      />

      {STEPS.map((step, index) => {
        const x = 80 + index * 380;
        const cx = x + 160;
        const clipId = `${uid}-card-${step.n}`;
        return (
          <g key={step.n}>
            <circle
              cx={cx}
              cy="268"
              r="42"
              fill="var(--color-bg-elevated)"
              stroke="var(--color-accent)"
              strokeWidth="4"
            />
            <text
              x={cx}
              y="278"
              textAnchor="middle"
              fill="var(--color-accent)"
              fontFamily="var(--font-mono)"
              fontSize="32"
              fontWeight="600"
            >
              {step.n}
            </text>

            <clipPath id={clipId}>
              <rect x={x} y="348" width="320" height="500" rx="20" />
            </clipPath>
            <g clipPath={`url(#${clipId})`}>
              <rect
                x={x}
                y="348"
                width="320"
                height="500"
                fill="var(--color-bg-elevated)"
              />
              {step.emphasis ? (
                <rect x={x} y="348" width="320" height="10" fill="var(--color-accent)" />
              ) : null}
            </g>
            <rect
              x={x}
              y="348"
              width="320"
              height="500"
              rx="20"
              fill="none"
              stroke={step.emphasis ? "var(--color-accent)" : "var(--color-border)"}
              strokeWidth={step.emphasis ? 2.5 : 1.5}
            />

            <text
              x={cx}
              y="430"
              textAnchor="middle"
              fill="var(--color-text)"
              fontFamily="var(--font-sans)"
              fontSize="42"
              fontWeight="600"
            >
              {step.title}
            </text>
            <text
              x={cx}
              y="478"
              textAnchor="middle"
              fill="var(--color-text-muted)"
              fontFamily="var(--font-mono)"
              fontSize="22"
            >
              {step.kicker}
            </text>

            {step.lines.map((line, lineIndex) => (
              <text
                key={line}
                x={cx}
                y={560 + lineIndex * 52}
                textAnchor="middle"
                fill="var(--color-text-secondary)"
                fontFamily="var(--font-sans)"
                fontSize="26"
              >
                {line}
              </text>
            ))}
          </g>
        );
      })}

      <text
        x="800"
        y="932"
        textAnchor="middle"
        fill="var(--color-text-muted)"
        fontFamily="var(--font-mono)"
        fontSize="24"
      >
        @orca-so/tx-sender  ·  orca_tx_sender  ·  TypeScript & Rust
      </text>
    </svg>
  );
}

const STEPS = [
  {
    n: "01",
    title: "App",
    kicker: "your program",
    lines: ["Instructions", "Wallet signer", "Optional ALTs"],
    emphasis: false,
  },
  {
    n: "02",
    title: "Prepare",
    kicker: "buildAndSend",
    lines: ["Estimate compute", "Dynamic priority fees", "Jito tip + margin"],
    emphasis: true,
  },
  {
    n: "03",
    title: "Send",
    kicker: "dual path",
    lines: ["RPC poll & resend", "Jito block engine", "Retry on failure"],
    emphasis: true,
  },
  {
    n: "04",
    title: "Land",
    kicker: "confirmed",
    lines: ["Signature returned", "Confirmed on Solana"],
    emphasis: false,
  },
] as const;
